import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, BellRing, Loader2, Plus, X } from "lucide-react";
import { postApi, useApi } from "@/lib/api";

const MAX_CITIES = 10;

const CityPicker = ({ cities, setCities, dark }) => {
  const [value, setValue] = useState("");
  const { data } = useApi("/events-cities");
  const known = Array.isArray(data) ? data : [];
  const add = () => {
    const c = value.trim();
    if (c && !cities.some((x) => x.toLowerCase() === c.toLowerCase()) && cities.length < MAX_CITIES) setCities([...cities, c]);
    setValue("");
  };
  return (
    <div className="mt-4" data-testid="newsletter-city-picker">
      <p className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-bold uppercase tracking-[0.15em] ${dark ? "text-black" : "text-white"}`}>
        <BellRing className="w-4 h-4" /> Alerte concert près de chez moi <span className="font-normal normal-case tracking-normal opacity-80">(facultatif)</span>
      </p>
      <div className="flex mt-2 border-2 border-white/70 max-w-md">
        <input list="newsletter-cities" value={value} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ta ville (Lyon, Paris…)" maxLength={80} data-testid="newsletter-city-input"
          className="flex-1 min-w-0 bg-transparent text-white placeholder:text-white/70 px-4 py-3 outline-none" />
        <button type="button" onClick={add} aria-label="Ajouter la ville" data-testid="newsletter-city-add-btn"
          className="px-4 text-white hover:bg-white hover:text-black transition-colors duration-200"><Plus className="w-5 h-5" /></button>
      </div>
      <datalist id="newsletter-cities">{known.map((c) => <option key={c} value={c} />)}</datalist>
      {cities.length > 0 && (
        <ul className="flex flex-wrap gap-2 mt-3" data-testid="newsletter-city-list">
          {cities.map((c) => (
            <li key={c} className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-1.5" data-testid={`newsletter-city-chip-${c}`}>
              {c}
              <button type="button" onClick={() => setCities(cities.filter((x) => x !== c))} aria-label={`Retirer ${c}`}
                data-testid={`newsletter-city-remove-${c}`} className="hover:text-primary"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MESSAGES = {
  subscribed: "Inscription confirmée. Bienvenue dans la famille LGTDZ !",
  updated: "C'est noté : tu recevras une alerte pour les concerts dans tes villes.",
  already_subscribed: "Tu es déjà inscrit·e. Merci !",
};

export const NewsletterForm = ({ dark = false }) => {
  const [email, setEmail] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await postApi("/newsletter", { email, cities });
      toast.success(MESSAGES[res?.status] || MESSAGES.subscribed);
      setEmail("");
      setCities([]);
    } catch {
      toast.error("Adresse email invalide.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-2xl" data-testid="newsletter-form">
      <div className="flex flex-col sm:flex-row border-2 border-white">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ton@email.com"
          data-testid="newsletter-email-input"
          className="flex-1 bg-transparent text-white placeholder:text-zinc-300 px-6 py-5 text-lg outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-btn"
          className="bg-white text-black font-display uppercase text-xl px-8 py-5 flex items-center justify-center gap-3 hover:bg-primary hover:text-white transition-colors duration-200 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>S'inscrire <ArrowRight className="w-5 h-5" /></>}
        </button>
      </div>
      <CityPicker cities={cities} setCities={setCities} dark={dark} />
    </form>
  );
};

export const NewsletterSection = () => (
  <section id="newsletter" className="bg-primary relative grain overflow-hidden" data-testid="newsletter-section">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-end relative">
      <h2 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.9] text-white">
        Reste<br />
        <span className="text-black">branché·e</span>
      </h2>
      <div>
        <p className="text-white text-lg mb-8 max-w-xl">
          Les concerts, les arrivages et les nouvelles émissions de la radio, directement dans ta boîte mail. Ajoute ta ville pour être prévenu·e dès qu'un concert LGTDZ y est annoncé.
        </p>
        <NewsletterForm dark />
      </div>
    </div>
  </section>
);
